import React, { FC, PropsWithChildren } from 'react';

import * as Styled from './Message.styles';
import * as O from 'fp-ts/Option';
import { pipe } from 'fp-ts/function';
import { HttpError } from '@core/http';
import { useTranslations } from '@shared/modules/translation/hooks';
import { renderOptional } from '@shared/utils/render';

interface MessageProps {
  type: 'error' | 'success';
}

const Message: FC<PropsWithChildren<MessageProps>> = ({ type, children }) => (
  <Styled.MessageContainer type={type}>{children}</Styled.MessageContainer>
);

interface HttpErrorMessageProps<E = unknown> {
  error: O.Option<HttpError<E>>;
  labelBuilder?: string | null;
}

export const HttpErrorMessage: FC<PropsWithChildren<HttpErrorMessageProps>> = ({ error, labelBuilder, children }) => {
  const translations = useTranslations();

  const message = pipe(
    error,
    O.map(err =>
      pipe(
        O.fromNullable(labelBuilder),
        O.getOrElse(() => translations.error.message.default),
      ),
    ),
  );

  return renderOptional(message, message => (
    <Styled.ErrorMessageContainer>
      {message} {children}
    </Styled.ErrorMessageContainer>
  ));
};

interface HttpSuccessMessageProps {
  labelBuilder?: string | null;
}

export const HttpSuccessMessage: FC<HttpSuccessMessageProps> = ({ labelBuilder }) => {
  const translations = useTranslations();

  const message = pipe(
    O.fromNullable(labelBuilder),
    O.getOrElse(() => translations.success.message.default),
  );

  return <Styled.SuccessMessageContainer>{message}</Styled.SuccessMessageContainer>;
};

export default Message;
